/**
 * Shift patterns — the arithmetic over a window.
 *
 * The file `shiftMasterData.js` became, as its own header said it would when the
 * pattern endpoint landed: "this file becomes `features/shifts/patterns.js` and
 * the page does not change". It landed. The page did change, because the API is
 * narrower than the design, but the arithmetic below did not — it is pure, it
 * was already right, and it is the part worth keeping.
 *
 * ── Why the maths stays in the client ────────────────────────────────────
 * Overlap detection and current-shift resolution are not stored facts. They are
 * questions asked of the pattern list as it stands on screen, and the drawer
 * asks them on every keystroke — a window that collides is something you find
 * out while typing the time, not after saving it. A server round trip per
 * keystroke would answer the same question slower and no more truthfully.
 *
 * ── Days are numbers here and strings on the wire ────────────────────────
 * Everything below counts days as `Date#getDay` does: 0 is Sunday. The API says
 * `mon`…`sun`. Mapped at the wire seam in `api.js` and nowhere else, so the
 * arithmetic never has to know and `(day + 6) % 7` still means yesterday.
 *
 * ── Grace is carried and is always zero ──────────────────────────────────
 * `resolveCurrentPattern` still takes a grace window, because the concept is
 * correct and the design is built around it — a login a minute before six
 * o'clock is the six o'clock shift. The API has no field for it, so nothing
 * supplies one and it defaults to 0. Left in rather than torn out: restoring it
 * is a column on the server and a picker in the drawer, not a rewrite of this.
 * See BACKEND-GAPS.md.
 */

// ── DEMO DATA, superseded by `/v1/shifts` ─────────────────────────────────
// Kept, not deleted. The sites are the real hierarchy's now (the page reads
// `useHierarchyChildren`), the operators are the real directory's, and the
// patterns are rows in the database. What has NO server field behind it is the
// icon, the tone, the grace window and the crew line — so the presets, the icon
// vocabulary and the tone list below are what a restore would start from if any
// of those columns ever land.
//
// `PATTERN_PRESETS` is the near-miss worth explaining: the API's `type` enum
// (day/morning/evening/night/general/rotating) covers what the presets were FOR
// — naming a window and giving it a look — so the page derives icon and tone
// from `type` instead, and the preset list is dead. See `TYPE_LOOK` in api.js.

// /**
//  * Shift Master — the pattern store, and the arithmetic over it.
//  *
//  * DEMO. Hardcoded here rather than in a fixture: nothing on the server models a
//  * shift PATTERN yet, and the roster endpoint that does exist returns worked
//  * shifts, not the rota that produced them. When the pattern endpoint lands this
//  * file becomes `features/shifts/patterns.js` and the page does not change.
//  *
//  * Everything below is data — site ids, people, line names, pattern names the
//  * user types — so none of it goes through `t()`. The furniture around it does.
//  *
//  * The prototype carries a `col: "#F59E0B"` on every pattern. A hex cannot
//  * survive `data-theme` or colour-blind mode, so a pattern carries a TONE id
//  * instead and the page resolves it through the static maps in `lib/tone.js`.
//  */
//
// /** Tones a pattern may wear. Violet is the night shift here, as `SHIFT_TYPE_TONE` already has it. */
// export const PATTERN_TONES = ['amber', 'forge', 'violet', 'green', 'ack', 'watch']
//
// export const SHIFT_SITES = [
//   { id: 'site_findlay', label: 'Findlay Plant', timeZone: 'America/New_York' },
//   { id: 'site_weissenthurm', label: 'Weißenthurm Works', timeZone: 'Europe/Berlin' },
//   { id: 'site_pune', label: 'Pune Plant', timeZone: 'Asia/Kolkata' },
// ]
//
// export const SITE_TIME_ZONE = Object.fromEntries(
//   SHIFT_SITES.map((site) => [site.id, site.timeZone]),
// )
//
// /** The icon vocabulary the drawer offers. Every id exists in `IconSprite`. */
// export const PATTERN_ICONS = ['sun', 'gauge', 'moon', 'cal', 'gear', 'bolt']
//
// /** Presets. Picking one fills the name, the icon, the tone and both times. */
// export const PATTERN_PRESETS = [
//   { name: 'Day', icon: 'sun', tone: 'amber', start: '06:00', end: '14:00' },
//   { name: 'Evening', icon: 'gauge', tone: 'forge', start: '14:00', end: '22:00' },
//   { name: 'Night', icon: 'moon', tone: 'violet', start: '22:00', end: '06:00' },
//   { name: 'General', icon: 'cal', tone: 'green', start: '09:00', end: '18:00' },
// ]
//
// export const GRACE_OPTIONS = [0, 5, 10, 15, 30]
//
// /** Sunday-first, matching `Date#getDay`. */
// export const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0]
//
// export const SHIFT_LINES = ['All lines', 'Line 1', 'Line 2', 'Ends Line 1', 'Compressed Air']
//
// export const SHIFT_OPERATORS = [
//   { id: 'usr_op_1', name: 'Arjun Mehta', role: 'Operator', initials: 'AM' },
//   { id: 'usr_mgr_1', name: 'Dana Okafor', role: 'Plant manager', initials: 'DO' },
//   { id: 'usr_rel_1', name: 'Ines Brandt', role: 'Reliability engineer', initials: 'IB' },
//   { id: 'usr_tech_1', name: 'Karan Patel', role: 'Technician', initials: 'KP' },
//   { id: 'usr_lead_1', name: 'Maya Reddy', role: 'Shift lead', initials: 'MR' },
//   { id: 'usr_coo_1', name: 'James Whitfield', role: 'COO', initials: 'JW' },
// ]
//
// export const INITIAL_PATTERNS = [
//   {
//     id: 'sp-day',
//     name: 'Day',
//     icon: 'sun',
//     tone: 'amber',
//     start: '06:00',
//     end: '14:00',
//     days: [1, 2, 3, 4, 5],
//     site: 'site_findlay',
//     grace: 15,
//     active: true,
//   },
//   {
//     id: 'sp-eve',
//     name: 'Evening',
//     icon: 'gauge',
//     tone: 'forge',
//     start: '14:00',
//     end: '22:00',
//     days: [1, 2, 3, 4, 5],
//     site: 'site_findlay',
//     grace: 15,
//     active: true,
//   },
//   {
//     id: 'sp-night',
//     name: 'Night',
//     icon: 'moon',
//     tone: 'violet',
//     start: '22:00',
//     end: '06:00',
//     days: [1, 2, 3, 4, 5, 6, 0],
//     site: 'site_findlay',
//     grace: 15,
//     active: true,
//   },
//   {
//     id: 'sp-wknd',
//     name: 'Weekend',
//     icon: 'cal',
//     tone: 'green',
//     start: '08:00',
//     end: '20:00',
//     days: [6, 0],
//     site: 'site_findlay',
//     grace: 30,
//     active: true,
//   },
//   {
//     id: 'sp-wtm-a',
//     name: 'Frühschicht',
//     icon: 'gear',
//     tone: 'ack',
//     start: '06:00',
//     end: '14:00',
//     days: [1, 2, 3, 4, 5, 6],
//     site: 'site_weissenthurm',
//     grace: 10,
//     active: true,
//   },
//   {
//     id: 'sp-pnq-a',
//     name: 'Shift A',
//     icon: 'gear',
//     tone: 'watch',
//     start: '07:00',
//     end: '15:00',
//     days: [1, 2, 3, 4, 5, 6],
//     site: 'site_pune',
//     grace: 10,
//     active: false,
//   },
// ]
//
// export const INITIAL_ROSTER = [
//   { userId: 'usr_op_1', patternId: 'sp-day', line: 'Line 1' },
//   { userId: 'usr_lead_1', patternId: 'sp-day', line: 'All lines' },
//   { userId: 'usr_tech_1', patternId: 'sp-eve', line: 'Line 2' },
//   { userId: 'usr_mgr_1', patternId: 'sp-night', line: 'All lines' },
//   { userId: 'usr_rel_1', patternId: 'sp-wtm-a', line: 'Ends Line 1' },
// ]
//
/**
 * Monday-first, Sunday last — the order a rota is read in, not `getDay`'s.
 *
 * The one constant that survived the block above, because it is not data: it is
 * how a week is laid out for a reader, and the API's `week_days` enum happens to
 * agree with it.
 */
export const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0]

/* ── arithmetic ──────────────────────────────────────────────────────────── */

/** `"22:00"` → 1320. Minutes since midnight. */
export function toMinutes(time) {
  const [hours, minutes] = (time || '00:00').split(':')
  return Number(hours) * 60 + (Number(minutes) || 0)
}

/** A window that ends at or before it starts has run over midnight. */
export function crossesMidnight(start, end) {
  return toMinutes(end) <= toMinutes(start)
}

/** Length of the window in whole hours and minutes, midnight-safe. */
export function patternDuration(start, end) {
  let total = toMinutes(end) - toMinutes(start)
  if (total <= 0) total += 1440
  return { hours: Math.floor(total / 60), minutes: total % 60, total }
}

/** The spans a window occupies on a 0–1440 clock, split where it wraps. */
function spansOf(start, end) {
  const from = toMinutes(start)
  const to = toMinutes(end)
  return crossesMidnight(start, end)
    ? [
        [from, 1440],
        [0, to],
      ]
    : [[from, to]]
}

/**
 * Patterns on the same site that share a day AND overlap in time.
 *
 * Both halves matter: two windows at the same hour on different days do not
 * conflict, and two windows on the same day that do not touch do not either.
 * A pattern never conflicts with itself, and an inactive one conflicts with
 * nothing — turning a pattern off is how you retire it without deleting it.
 */
export function overlappingPatterns(candidate, patterns) {
  const own = spansOf(candidate.start, candidate.end)
  return patterns.filter((other) => {
    if (other.id === candidate.id || !other.active) return false
    if (!sharesAPlant(candidate, other)) return false
    if (!other.days.some((day) => candidate.days.includes(day))) return false
    const theirs = spansOf(other.start, other.end)
    return own.some(([a0, a1]) => theirs.some(([b0, b1]) => a0 < b1 && b0 < a1))
  })
}

/**
 * Whether two patterns can ever apply at the same plant.
 *
 * `plantId: null` means EVERY plant — an account-level shift — so a null shares
 * ground with everything, including another null. Getting it wrong in the
 * obvious direction (reading null as "no plant") would silently stop reporting
 * the most important conflict there is, between two account-level patterns.
 *
 * Two patterns at two different plants cannot collide, which is the whole of the
 * remaining case now that a pattern belongs to one plant rather than a list.
 */
function sharesAPlant(a, b) {
  if (a.plantId == null || b.plantId == null) return true
  return a.plantId === b.plantId
}

/**
 * Which pattern a login at `when` resolves to on `siteId`.
 *
 * The grace window widens the match at both ends, so two patterns can claim the
 * same minute; the narrowest window wins, because the more specific pattern is
 * the one somebody wrote deliberately. A wrapped window is matched against the
 * PREVIOUS day for its morning half — the day selection refers to the shift's
 * start day, which is what the drawer says it does.
 */
export function resolveCurrentPattern(siteId, patterns, when = new Date()) {
  const day = when.getDay()
  const minutes = when.getHours() * 60 + when.getMinutes()
  const hits = []

  for (const pattern of patterns) {
    /* `plantId: null` applies everywhere, so an account-level pattern is a
       candidate at every site — the same rule `sharesAPlant` states above. */
    if (!pattern.active) continue
    if (siteId && pattern.plantId != null && pattern.plantId !== siteId) continue
    const start = toMinutes(pattern.start)
    const end = toMinutes(pattern.end)
    /* Always 0 today — the API has no grace column. See the header. */
    const grace = pattern.grace || 0

    if (!crossesMidnight(pattern.start, pattern.end)) {
      if (pattern.days.includes(day) && minutes >= start - grace && minutes < end + grace) {
        hits.push(pattern)
      }
      continue
    }
    if (pattern.days.includes(day) && minutes >= start - grace) hits.push(pattern)
    else if (pattern.days.includes((day + 6) % 7) && minutes < end + grace) hits.push(pattern)
  }

  if (!hits.length) return null
  hits.sort(
    (a, b) => patternDuration(a.start, a.end).total - patternDuration(b.start, b.end).total,
  )
  return hits[0]
}

/**
 * A new pattern, seeded for the site the reader is filtering by.
 *
 * `plantId: null` is the API's "every plant" and is the right default — a pattern
 * defined at the account is the common case, and narrowing it is the deliberate
 * act. Filtering by one site pre-selects it, since that is what the reader was
 * looking at when they pressed Add.
 *
 * A reader whose grant is one plant cannot create an account-level shift, so the
 * drawer corrects this seed rather than offering a default that would 403. The
 * default stays null here because this function does not know the grant, and
 * inventing a plant id would be worse than being corrected one layer up.
 *
 * `type` defaults to `day` rather than to the enum's first value for the reason
 * the asset-class form defaults to `machine`: it is far and away the most common
 * thing anyone defines, and the alternative is defaulting to whatever sorts
 * first.
 */
export function blankPattern(siteId) {
  return {
    id: null,
    name: '',
    type: 'day',
    start: '',
    end: '',
    days: [1, 2, 3, 4, 5],
    plantId: siteId && siteId !== 'all' ? siteId : null,
    active: true,
  }
}
